import React, { useState, useEffect } from 'react';
import { studentsAPI } from '../services/api';
import LecturerSidebar from '../components/LecturerSidebar';

const LecturerStudentsList = () => {
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    level: 'all',
    degree: 'all',
    group: 'all',
    year: 'all',
    semester: 'all',
    studentStatus: 'all',
    status: 'all'
  });

  // Dynamic filter options based on available data
  const [filterOptions, setFilterOptions] = useState({
    level: ['all'],
    degree: ['all'],
    group: ['all'],
    year: ['all'],
    semester: ['all'],
    studentStatus: ['all', 'local', 'international'],
    status: ['all', 'active', 'inactive']
  });

  // Fetch all students
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await studentsAPI.getAllStudents();
      const studentsData = response.data || [];
      setAllStudents(studentsData);
      setStudents(studentsData);
      
      // Generate dynamic filter options based on actual data
      generateFilterOptions(studentsData);
      
    } catch (error) {
      console.error('Error fetching students:', error);
      
      if (error.message?.includes('Lecturer not found in staff table')) {
        setError('Your lecturer account is not found in the staff table. Please contact your administrator to add your record with the correct university_id.');
      } else {
        setError(error.message || 'Failed to fetch students');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate filter options based on actual data
  const generateFilterOptions = (studentsData) => {
    const options = {
      level: ['all'],
      degree: ['all'],
      group: ['all'],
      year: ['all'],
      semester: ['all'],
      studentStatus: ['all', 'local', 'international'],
      status: ['all', 'active', 'inactive']
    };

    if (studentsData && studentsData.length > 0) {
      // Level options - include standard academic levels
      const levels = [...new Set(studentsData.map(s => s.degree_level || s.level).filter(Boolean))];
      const standardLevels = ['undergraduate', 'postgraduate', 'doctoral'];
      const combinedLevels = [...new Set([...levels, ...standardLevels])];
      options.level = ['all', ...combinedLevels.sort()];

      // Degree programme options
      const degrees = [...new Set(studentsData.map(s => s.degree_programme).filter(Boolean))];
      options.degree = ['all', ...degrees.sort()];

      // Group options
      const groups = [...new Set(studentsData.map(s => s.group_name || s.group_no).filter(Boolean))];
      options.group = ['all', ...groups.sort()];

      // Year options - include standard years 1-4
      const years = [...new Set(studentsData.map(s => s.current_year).filter(Boolean))];
      const standardYears = [1, 2, 3, 4];
      const combinedYears = [...new Set([...years, ...standardYears])];
      options.year = ['all', ...combinedYears.sort((a, b) => a - b)];

      // Semester options
      const semesters = [...new Set(studentsData.map(s => s.current_semester).filter(Boolean))];
      options.semester = ['all', ...semesters.sort((a, b) => a - b)];
    } else {
      // If no data, provide standard options
      options.level = ['all', 'undergraduate', 'postgraduate', 'doctoral'];
      options.year = ['all', 1, 2, 3, 4];
      options.semester = ['all', 1, 2];
      options.status = ['all', 'active', 'inactive'];
    }

    setFilterOptions(options);
  };

  // Apply filters to students
  const applyFilters = () => {
    let filteredStudents = [...allStudents];

    // Apply level filter
    if (filters.level && filters.level !== 'all') {
      filteredStudents = filteredStudents.filter(student => {
        const studentLevel = student.degree_level || student.level;
        return studentLevel === filters.level;
      });
    }

    // Apply degree filter
    if (filters.degree && filters.degree !== 'all') {
      filteredStudents = filteredStudents.filter(student => {
        return student.degree_programme === filters.degree;
      });
    }

    // Apply group filter
    if (filters.group && filters.group !== 'all') {
      filteredStudents = filteredStudents.filter(student => {
        const studentGroup = student.group_name || student.group_no;
        return studentGroup === filters.group;
      });
    }

    // Apply year filter
    if (filters.year && filters.year !== 'all') {
      filteredStudents = filteredStudents.filter(student => {
        return student.current_year?.toString() === filters.year;
      });
    }

    // Apply semester filter
    if (filters.semester && filters.semester !== 'all') {
      filteredStudents = filteredStudents.filter(student => {
        return student.current_semester?.toString() === filters.semester;
      });
    }

    // Apply student status filter
    if (filters.studentStatus && filters.studentStatus !== 'all') {
      filteredStudents = filteredStudents.filter(student => 
        (student.local_international || student.degree_path) === filters.studentStatus
      );
    }

    // Apply status filter (active/inactive)
    if (filters.status && filters.status !== 'all') {
      filteredStudents = filteredStudents.filter(student => {
        const isActive = student.active === true || student.active === 'true';
        return filters.status === 'active' ? isActive : !isActive;
      });
    }

    setStudents(filteredStudents);
  };

  // Initial fetch
  useEffect(() => {
    fetchStudents();
  }, []);

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Search function
  const handleSearch = () => {
    applyFilters();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      level: 'all',
      degree: 'all',
      group: 'all',
      year: 'all',
      semester: 'all',
      studentStatus: 'all',
      status: 'all'
    });
    setStudents(allStudents);
  };

  // Filter dropdown component
  const FilterDropdown = ({ label, name, options = ['all'], value = 'all' }) => (
    <div style={{position: 'relative', width: 164}}>
      <div style={{
        opacity: 0.65,
        color: '#444444',
        fontSize: 16,
        fontFamily: 'Outfit',
        marginBottom: 8
      }}>
        {label}
      </div>
      <select
        value={value}
        onChange={(e) => handleFilterChange(name, e.target.value)}
        style={{
          width: '100%',
          height: 58,
          background: 'white',
          border: '1px solid #CED4DA',
          borderRadius: 6,
          padding: '0 15px',
          fontSize: 16,
          fontFamily: 'Outfit'
        }}
      >
        {(options || ['all']).map(option => (
          <option key={option} value={option}>
            {option === 'all' ? 'All' : option}
          </option>
        ))}
      </select>
    </div>
  );

  const formatDisplayValue = (value) => {
    if (!value) return 'N/A';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  // Helper function to format Academic Year/Semester consistently
  const formatYearSemester = (student) => {
    const year = student.current_year;
    const semester = student.current_semester;
    
    // Ensure we always return numeric format like "1/1"
    let formattedYear = year ? year.toString() : 'N/A';
    let formattedSemester = semester ? semester.toString() : 'N/A';
    
    // Convert any month names to numbers if needed
    if (semester && typeof semester === 'string') {
      // If semester contains month names, convert to numbers
      const monthMap = {
        'jan': '1', 'january': '1',
        'feb': '2', 'february': '2', 
        'mar': '3', 'march': '3',
        'apr': '4', 'april': '4',
        'may': '5',
        'jun': '6', 'june': '6',
        'jul': '7', 'july': '7',
        'aug': '8', 'august': '8',
        'sep': '9', 'september': '9',
        'oct': '10', 'october': '10',
        'nov': '11', 'november': '11',
        'dec': '12', 'december': '12'
      };
      
      const lowerSemester = semester.toLowerCase();
      if (monthMap[lowerSemester]) {
        formattedSemester = monthMap[lowerSemester];
      }
    }
    
    return `${formattedYear}/${formattedSemester}`;
  };

  // CSV Export function
  const exportToCSV = () => {
    if (students.length === 0) {
      alert('No students to export. Please adjust your filters or ensure there are students in the system.');
      return;
    }

    try {
      // Log the first student's data for debugging
      if (students.length > 0) {
        console.log('Sample student data for CSV export:', {
          current_year: students[0].current_year,
          current_semester: students[0].current_semester,
          formatted: formatYearSemester(students[0])
        });
      }

      // Define CSV headers
      const headers = [
        'Student ID',
        'First Name', 
        'Last Name',
        'Email',
        'Academic Level',
        'Degree Programme',
        'Group',
        'Academic Year/Semester',
        'Local/International',
        'Status'
      ];

      // Convert students data to CSV rows
      const csvData = students.map(student => {
        return [
          student.student_id || 'N/A',
          student.first_name || 'N/A',
          student.last_name || 'N/A',
          student.email || 'N/A',
          student.degree_level || student.level || 'N/A',
          student.degree_programme || 'N/A',
          student.group_name || student.group_no || 'N/A',
          formatYearSemester(student),
          formatDisplayValue(student.local_international || student.degree_path),
          student.active ? 'Active' : 'Inactive'
        ];
      });

      // Combine headers and data
      const allData = [headers, ...csvData];

      // Convert to CSV string
      const csvContent = allData.map(row => 
        row.map(cell => 
          // Escape quotes and wrap in quotes if cell contains comma, quote, or newline
          typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))
            ? `"${cell.replace(/"/g, '""')}"` 
            : cell
        ).join(',')
      ).join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Generate filename with current date and filter info
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
        let filename = `students_export_${dateStr}`;
        
        // Add filter info to filename if filters are applied
        const activeFilters = Object.entries(filters)
          .filter(([key, value]) => value !== 'all')
          .map(([key, value]) => `${key}-${value}`)
          .join('_');
        
        if (activeFilters) {
          filename += `_filtered_${activeFilters}`;
        }
        
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Show success message
        console.log(`Successfully exported ${students.length} students to CSV`);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: 'white'
    }}>
      <LecturerSidebar />
      
      <div style={{
        flex: 1,
        marginLeft: 290,
        padding: '0 40px 40px',
        overflow: 'auto'
      }}>
        
        {/* Page Title */}
        <div style={{ marginTop: 40 }}>
          <h1 style={{
            fontSize: 24,
            fontFamily: 'Outfit',
            fontWeight: '700',
            color: '#333',
            marginBottom: 8
          }}>
            Students List
          </h1>
          <p style={{
            fontSize: 16,
            fontFamily: 'Outfit',
            color: '#666',
            margin: 0
          }}>
            View and manage students in your university
          </p>
        </div>

        {/* Filters Section */}
        <div style={{
          marginTop: 30,
          background: '#FFFFFF',
          borderRadius: 8,
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
            <h3 style={{
              fontSize: 18,
              fontFamily: 'Outfit',
              fontWeight: '600',
              marginBottom: 20,
              color: '#333'
            }}>
              Filter Students
            </h3>
            
            <div style={{
              display: 'flex',
              gap: 20,
              alignItems: 'flex-end',
              flexWrap: 'wrap'
            }}>
            <FilterDropdown 
              label="Academic Level" 
              name="level" 
              options={filterOptions.level}
              value={filters.level}
            />
            <FilterDropdown 
              label="Degree Programme" 
              name="degree" 
              options={filterOptions.degree}
              value={filters.degree}
            />
            <FilterDropdown 
              label="Group" 
              name="group" 
              options={filterOptions.group}
              value={filters.group}
            />
            <FilterDropdown 
              label="Year" 
              name="year" 
              options={filterOptions.year}
              value={filters.year}
            />
            <FilterDropdown 
              label="Semester" 
              name="semester" 
              options={filterOptions.semester}
              value={filters.semester}
            />
            <FilterDropdown 
              label="Local/International" 
              name="studentStatus" 
              options={filterOptions.studentStatus}
              value={filters.studentStatus}
            />
            <FilterDropdown 
              label="Status" 
              name="status" 
              options={filterOptions.status}
              value={filters.status}
            />
            
            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                onClick={handleSearch}
                style={{
                  width: 120,
                  height: 52,
                  background: '#F04915',
                  borderRadius: 8,
                  border: 'none',
                  color: 'white',
                  fontSize: 16,
                  fontFamily: 'Outfit',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#D63A0F'}
                onMouseOut={(e) => e.target.style.background = '#F04915'}
              >
                Search
              </button>
              
              <button 
                onClick={resetFilters}
                style={{
                  width: 120,
                  height: 52,
                  background: '#6B7280',
                  borderRadius: 8,
                  border: 'none',
                  color: 'white',
                  fontSize: 16,
                  fontFamily: 'Outfit',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#4B5563'}
                onMouseOut={(e) => e.target.style.background = '#6B7280'}
              >
                Reset
              </button>
              
              <button 
                onClick={exportToCSV}
                style={{
                  width: 140,
                  height: 52,
                  background: '#10B981',
                  borderRadius: 8,
                  border: 'none',
                  color: 'white',
                  fontSize: 16,
                  fontFamily: 'Outfit',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
                onMouseOver={(e) => e.target.style.background = '#059669'}
                onMouseOut={(e) => e.target.style.background = '#10B981'}
                title={`Export ${students.length} student${students.length !== 1 ? 's' : ''} to CSV`}
              >
                <span>📥</span>
                Export CSV
              </button>
            </div>
          </div>

        {/* Students Table */}
        <div style={{marginTop: 30}}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: '#F9FAFB',
              borderRadius: 8,
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: 18, fontFamily: 'Outfit', color: '#6B7280' }}>
                Loading students...
              </div>
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: '#FEF2F2',
              borderRadius: 8,
              border: '1px solid #FECACA'
            }}>
              <div style={{ fontSize: 18, fontFamily: 'Outfit', color: '#DC2626' }}>
                {error}
              </div>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {students.length === 0 ? (
                <div style={{
                  textAlign: 'center', 
                  padding: '60px 20px',
                  background: '#F9FAFB'
                }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>👨‍🎓</div>
                  <div style={{
                    fontSize: 20,
                    fontWeight: '600',
                    marginBottom: 12,
                    color: '#374151',
                    fontFamily: 'Outfit'
                  }}>
                    No Students Found
                  </div>
                  <div style={{
                    color: '#6B7280',
                    marginBottom: 24,
                    lineHeight: 1.6,
                    fontFamily: 'Outfit'
                  }}>
                    {allStudents.length === 0 
                      ? "Your university currently has no students in the system."
                      : "No students match your current filter criteria."
                    }
                  </div>
                  
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <button 
                      onClick={resetFilters}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#6B7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}
                    >
                      Clear Filters
                    </button>
                    
                    <button 
                      onClick={fetchStudents}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#2563EB',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}
                    >
                      Refresh Data
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Results Summary */}
                  <div style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #E5E7EB',
                    background: '#F9FAFB'
                  }}>
                    <div style={{
                      fontSize: 16,
                      fontFamily: 'Outfit',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      Showing {students.length} of {allStudents.length} students
                    </div>
                  </div>

                  {/* Table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: '#F9FAFB' }}>
                        <tr>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>No.</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Student ID</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Name</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Degree Programme</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Level</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Group</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Year/Semester</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Local/Inter</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((student, index) => (
                          <tr key={student.id || index} style={{
                            borderBottom: '1px solid #F3F4F6'
                          }}>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {(index + 1).toString().padStart(2, '0')}.
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              fontWeight: '500',
                              color: '#111827'
                            }}>
                              {student.student_id || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              fontWeight: '500',
                              color: '#111827'
                            }}>
                              {student.name || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {student.degree_programme || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                background: '#EBF8FF',
                                color: '#1E40AF',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: '500'
                              }}>
                                {formatDisplayValue(student.degree_level || student.level) || 'N/A'}
                              </span>
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {student.group_name || student.group_no || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {formatYearSemester(student)}
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                background: (student.local_international || student.degree_path) === 'local' ? '#F0FDF4' : '#FEF3C7',
                                color: (student.local_international || student.degree_path) === 'local' ? '#166534' : '#92400E',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: '500'
                              }}>
                                {formatDisplayValue(student.local_international || student.degree_path)}
                              </span>
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                background: student.active ? '#F0FDF4' : '#FEF2F2',
                                color: student.active ? '#166534' : '#DC2626',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: '500'
                              }}>
                                {student.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default LecturerStudentsList;
